import { Close, Search, Star } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import ImageModal from "../../components/ImageModa";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import ProtectedPermissionView from "../../components/ProtectedPermissionView";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import blogService from "../../services/blog";
import config from "../../utils/config";
import { generatePublicUrl } from "../../utils/generatePublicUrl";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function BlogList() {
  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({
    keyword: "",
  });
  const [imageUrl, setImageUrl] = useState("");
  const [open, setOpen] = useState(false);
  const notification = useNotification();
  const deleteBlog = useMutation("delete-blog", blogService.deleteBlog, {
    onSuccess: () => {
      listBlog.refetch();
      notification.onOpen({
        message: "Blog was successfully deleted!",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      listBlog.refetch();
      notification.onOpen({
        message:
          error?.response?.data?.message === undefined
            ? "Something went wrong"
            : error?.response?.data?.message,
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });
  const onDelete = (id: number) => {
    alert.onClose();
    deleteBlog.mutate(id);
  };
  const alert = useAlert();
  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this blog?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };
  const listBlog = useQuery(
    ["blog_list", options],
    ({ queryKey }) => {
      return blogService.getAllBlog({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  const handleImageClick = (url: string) => {
    setOpen(true);
    setImageUrl(url);
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Blog List</title>
      </Helmet>

      <Breadcrumb
        label={`Blog List`}
        breadcrumbs={[{ label: "Blog Management", href: "/app/cms/blog" }]}
      />

      <ProtectedPermissionPage
        menuSlug="blog"
        requiredPermissions={{ is_read: true }}
      >
        <Box sx={{ mt: 3 }}>
          <ImageModal
            open={open}
            src={imageUrl}
            onClose={() => setOpen(false)}
          />
          <Stack direction="row" spacing={1}>
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <ProtectedPermissionView
            requiredPermissions={{ is_create: true }}
            menuSlug="blog"
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/cms/blog/create"
              >
                ADD BLOG
              </Button>
            </Stack>
          </ProtectedPermissionView>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listBlog.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listBlog, "data.data", [])}
            rowCount={get(listBlog, "data.data", []).length}
            page={options.page_number}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "title",
                headerName: "Title",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <>
                      {params.row.is_carousel && (
                        <Star sx={{ mr: 1 }} color="warning" />
                      )}
                      <Stack>
                        <Typography
                          sx={{ textDecoration: "none" }}
                          component={RouterLink}
                          to={`/app/cms/blog/detail/${get(params, "row.id")}`}
                        >
                          {params.value}
                        </Typography>
                      </Stack>
                    </>
                  );
                },
              },
              {
                field: "short_description",
                headerName: "Short Description",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "tag",
                headerName: "Tags",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "banner",
                headerName: "Banner",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Box
                        component="img"
                        src={generatePublicUrl(params.value)}
                        alt="Banner"
                        sx={{
                          height: "50px",
                          "&:hover": {
                            cursor: "pointer",
                          },
                        }}
                        onClick={() =>
                          handleImageClick(generatePublicUrl(params.value))
                        }
                      />
                    </Stack>
                  );
                },
              },
              {
                field: "views",
                headerName: "Total Views",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <ProtectedPermissionView
                        menuSlug="blog"
                        requiredPermissions={{ is_update: true }}
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          component={RouterLink}
                          to={`/app/cms/blog/edit/${get(params, "row.id")}`}
                        >
                          Edit
                        </Button>
                      </ProtectedPermissionView>

                      <ProtectedPermissionView
                        menuSlug="blog"
                        requiredPermissions={{ is_delete: true }}
                      >
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => onClickDelete(params.row.id)}
                        >
                          Delete
                        </Button>
                      </ProtectedPermissionView>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
